import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Kpi } from '@/types/dashboards';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

/** Rules charted in the summary strip above the grid. */
const TOP_N = 10;

export interface RuleHealthPanelProps {
  kpis: Kpi[] | undefined;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
}

/**
 * Rule Health panel — `GET /api/v1/dashboards/rule-health`. Exactly one `Kpi`
 * per `rule_id`: `value` is that rule's mean fires/week over the trailing
 * 12 weeks OF DATA (the endpoint anchors on `max(alert_time)`, never `now()`),
 * and `story` folds the last-full-week figure, dormancy, WoW delta,
 * suppression share, and estimated analyst-hours burned into one narrative.
 *
 * The wire shape does not carry a per-rule 12-week series (Task 3 folds it
 * into `story` rather than exposing it as its own field), so the strip above
 * the grid is a TOP-N leaderboard of the busiest rules — not a bar for every
 * rule, which just restated the card grid underneath it.
 */
export function RuleHealthPanel({
  kpis,
  isLoading,
  error,
  emptyMessage = 'No rule fires recorded in the trailing 12 weeks.',
}: RuleHealthPanelProps): JSX.Element {
  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-3 text-sm text-red-800">
          Could not load rule health: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="space-y-2 p-4">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!kpis || kpis.length === 0) {
    return (
      <Card>
        <CardContent className="py-3 text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  // Defensive dedupe: the endpoint keys its response by rule_id, so a repeat
  // here would mean a regression upstream. Rendering the first occurrence
  // keeps one card per rule rather than silently doubling the grid.
  const uniqueKpis = Array.from(new Map(kpis.map((kpi) => [kpi.name, kpi])).values());

  // The summary strip is the TOP 10 only. Charting every rule made the header
  // a second, unlabelled copy of the card grid directly below it — the same
  // rule set rendered twice, which read as duplicated rules.
  const topKpis = uniqueKpis
    .filter((kpi) => typeof kpi.value === 'number' && kpi.value > 0)
    .slice(0, TOP_N);

  return (
    <div className="space-y-3">
      {topKpis.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Busiest rules — top {topKpis.length} by fires/week
            </CardTitle>
          </CardHeader>
          <CardContent className="h-28 pt-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topKpis.map((kpi) => ({
                  rule_id: kpi.name,
                  fires: typeof kpi.value === 'number' ? kpi.value : 0,
                }))}
                margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
              >
                <XAxis dataKey="rule_id" tick={{ fontSize: 10 }} interval={0} />
                <Tooltip
                  formatter={(value: number) => [`${NUM.format(value)} fires/wk`, '']}
                  labelFormatter={(label: string) => label}
                />
                <Bar dataKey="fires" fill="hsl(215 70% 55%)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {uniqueKpis.map((kpi) => (
          <Card key={kpi.name}>
            <CardContent className="space-y-1.5 p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {kpi.name}
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {typeof kpi.value === 'number' ? NUM.format(kpi.value) : (kpi.value ?? '—')}
                {kpi.unit ? <span className="ml-1 text-sm font-normal text-muted-foreground">{kpi.unit}</span> : null}
              </div>
              <p className="text-sm text-muted-foreground">{kpi.story}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default RuleHealthPanel;
